/**
 * Build CoT XML strings for all TAK shape types.
 * Each builder produces a complete <event> XML string ready to POST to TAK Server.
 */

import type { GeoPoint, RouteWaypoint } from '../types/drawing.js';
import { SHAPE_COT_TYPES } from '../constants/drawing-types.js';
import { xmlAttr, xmlSelfClosing } from './xml-helpers.js';

/** Parameters shared by all shape builders */
interface ShapeBuildParams {
  uid: string;
  callsign: string;
  strokeColor: number;
  strokeWeight: number;
  fillColor: number;
}

/** Parameters for building a polygon CoT event */
export interface PolygonBuildParams extends ShapeBuildParams {
  vertices: GeoPoint[];
}

/** Parameters for building a circle CoT event */
export interface CircleBuildParams extends ShapeBuildParams {
  center: GeoPoint;
  radiusMeters: number;
}

/** Parameters for building a route CoT event */
export interface RouteBuildParams extends ShapeBuildParams {
  waypoints: RouteWaypoint[];
  planningMethod: string;
  routeMethod: string;
  direction: string;
  routeType: string;
}

/** Parameters for building a geofence CoT event */
export interface GeofenceBuildParams extends ShapeBuildParams {
  center: GeoPoint;
  radiusMeters: number;
  trigger: 'Entry' | 'Exit' | 'Both';
  monitor: string;
  tracking: boolean;
  elevationMonitored: boolean;
  minElevation: number;
  maxElevation: number;
}

/** Parameters for building a measurement CoT event */
export interface MeasurementBuildParams extends ShapeBuildParams {
  origin: GeoPoint;
  endpoint: GeoPoint;
  rangeMeters: number;
  bearingDegrees: number;
  rangeUnits: number;
  bearingUnits: number;
  northRef: number;
}

/** Build CoT XML for a polygon shape */
export function buildPolygonCotXml(params: PolygonBuildParams): string {
  const center = computeCentroid(params.vertices);
  const links = params.vertices.map((v) => buildLinkPoint(v)).join('\n    ');
  return buildShapeEvent(params.uid, SHAPE_COT_TYPES.POLYGON, center, params, links);
}

/** Build CoT XML for a rectangle shape */
export function buildRectangleCotXml(params: PolygonBuildParams): string {
  const center = computeCentroid(params.vertices);
  const links = params.vertices.map((v) => buildLinkPoint(v)).join('\n    ');
  return buildShapeEvent(params.uid, SHAPE_COT_TYPES.RECTANGLE, center, params, links);
}

/** Build CoT XML for a freeform line shape */
export function buildFreeformCotXml(params: PolygonBuildParams): string {
  const center = computeCentroid(params.vertices);
  const links = params.vertices.map((v) => buildLinkPoint(v)).join('\n    ');
  return buildShapeEvent(params.uid, SHAPE_COT_TYPES.FREEFORM, center, params, links);
}

/** Build CoT XML for a circle shape */
export function buildCircleCotXml(params: CircleBuildParams): string {
  const { uid, center, radiusMeters } = params;
  const ellipse = xmlSelfClosing('ellipse', {
    major: radiusMeters,
    minor: radiusMeters,
  });
  return buildShapeEvent(uid, SHAPE_COT_TYPES.CIRCLE, center, params, ellipse);
}

/** Build CoT XML for a route with waypoints */
export function buildRouteCotXml(params: RouteBuildParams): string {
  const { uid, waypoints, planningMethod, routeMethod, direction, routeType } = params;
  const center = waypoints.length > 0 ? waypoints[0]!.position : { lat: 0, lon: 0 };

  const linkAttr = xmlSelfClosing('link_attr', {
    planningmethod: planningMethod,
    method: routeMethod,
    direction,
    routetype: routeType,
    order: 'Ascending Check Points',
  });

  const waypointLinks = waypoints.map((wp, i) => {
    const wpType = resolveWaypointType(i, waypoints.length);
    return [
      xmlSelfClosing('link', {
        uid: wp.uid,
        callsign: wp.callsign,
        type: wpType,
        point: formatPointAttr(wp.position),
        relation: 'c',
      }),
    ].join('');
  }).join('\n    ');

  const detailContent = `${linkAttr}\n    ${waypointLinks}`;
  return buildShapeEvent(uid, SHAPE_COT_TYPES.ROUTE, center, params, detailContent);
}

/** Build CoT XML for a geofence */
export function buildGeofenceCotXml(params: GeofenceBuildParams): string {
  const { uid, center, radiusMeters, trigger, monitor, tracking } = params;
  const { elevationMonitored, minElevation, maxElevation } = params;

  const ellipse = xmlSelfClosing('ellipse', {
    major: radiusMeters,
    minor: radiusMeters,
  });

  const geofenceEl = xmlSelfClosing('__geofence', {
    trigger,
    monitor,
    tracking: tracking ? 'true' : 'false',
    elevationMonitored: elevationMonitored ? 'true' : 'false',
    minElevation,
    maxElevation,
  });

  return buildShapeEvent(uid, SHAPE_COT_TYPES.CIRCLE, center, params, `${ellipse}\n    ${geofenceEl}`);
}

/** Build CoT XML for a range and bearing measurement */
export function buildMeasurementCotXml(params: MeasurementBuildParams): string {
  const { uid, origin, rangeMeters, bearingDegrees, rangeUnits, bearingUnits, northRef } = params;

  const rangeEl = `<range>${rangeMeters}</range>`;
  const bearingEl = `<bearing>${bearingDegrees}</bearing>`;
  const rangeUnitsEl = `<rangeUnits>${rangeUnits}</rangeUnits>`;
  const bearingUnitsEl = `<bearingUnits>${bearingUnits}</bearingUnits>`;
  const northRefEl = `<northRef>${northRef}</northRef>`;

  const detailContent = [rangeEl, bearingEl, rangeUnitsEl, bearingUnitsEl, northRefEl].join('\n    ');
  return buildShapeEvent(uid, SHAPE_COT_TYPES.RANGE_BEARING, origin, params, detailContent);
}

/* ─── Internal Helpers ─── */

/** Build the full <event> wrapper with shared detail elements */
function buildShapeEvent(
  uid: string,
  cotType: string,
  center: GeoPoint,
  params: ShapeBuildParams,
  detailContent: string,
): string {
  const now = new Date().toISOString();
  const stale = new Date(Date.now() + 300_000).toISOString();

  const eventAttrs = [
    xmlAttr('version', '2.0'),
    xmlAttr('uid', uid),
    xmlAttr('type', cotType),
    xmlAttr('time', now),
    xmlAttr('start', now),
    xmlAttr('stale', stale),
    xmlAttr('how', 'h-g-i-g-o'),
  ].join(' ');

  const pointAttrs = [
    xmlAttr('lat', center.lat),
    xmlAttr('lon', center.lon),
    xmlAttr('hae', 0),
    xmlAttr('ce', 9999999),
    xmlAttr('le', 9999999),
  ].join(' ');

  const styleEls = buildStyleElements(params.strokeColor, params.strokeWeight, params.fillColor);
  const contactEl = xmlSelfClosing('contact', { callsign: params.callsign });

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<event ${eventAttrs}>`,
    `  <point ${pointAttrs} />`,
    '  <detail>',
    `    ${contactEl}`,
    `    ${styleEls}`,
    `    ${detailContent}`,
    '  </detail>',
    '</event>',
  ].join('\n');
}

/** Build <strokeColor>, <strokeWeight>, <fillColor> elements */
function buildStyleElements(stroke: number, weight: number, fill: number): string {
  return [
    `<strokeColor value="${stroke}" />`,
    `<strokeWeight value="${weight}" />`,
    `<fillColor value="${fill}" />`,
  ].join('\n    ');
}

/** Build a <link point="lat,lon,hae" /> element for a vertex */
function buildLinkPoint(point: GeoPoint): string {
  return xmlSelfClosing('link', { point: `${point.lat},${point.lon},0` });
}

/** Format a GeoPoint as "lat,lon,hae" for link point attributes */
function formatPointAttr(point: GeoPoint): string {
  return `${point.lat},${point.lon},0`;
}

/** Compute the centroid of a set of vertices */
function computeCentroid(vertices: GeoPoint[]): GeoPoint {
  if (vertices.length === 0) return { lat: 0, lon: 0 };

  let latSum = 0;
  let lonSum = 0;
  for (const v of vertices) {
    latSum += v.lat;
    lonSum += v.lon;
  }
  return { lat: latSum / vertices.length, lon: lonSum / vertices.length };
}

/** Determine the CoT type for a waypoint based on its position in the route */
function resolveWaypointType(index: number, total: number): string {
  if (index === 0) return SHAPE_COT_TYPES.WAYPOINT;
  if (index === total - 1) return SHAPE_COT_TYPES.WAYPOINT;
  return SHAPE_COT_TYPES.CONTROL_POINT;
}
